import { Button, Icon, Tooltip } from "@deligoo/ui"
import clsx from "clsx"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { batch, shallowEqual, useDispatch } from "react-redux"

import { useAppSelector } from "@/App"
import { DriverHeader } from "@/components/DriverHeader"
import { DriverOverview } from "@/components/DriverOverview"
import { OrderProgressBar } from "@/components/OrderProgressBar"
import { User } from "@/components/User"
import { selectDriverById } from "@/store/drivers"
import { selectOrderById } from "@/store/orders"
import { setFocusedDriver, setFocusedOrder, setFocusedOrderArea } from "@/store/ui"
import { Order } from "@/types"
import { DriverUtils, OrderUtils } from "@/utils"
import { MapRoute, useMapViewportSavedOnMount } from "@/views/MapView"

import cls from "./AboutOrder.module.scss"

type AboutOrderProps = {
  id: Order["id"]
  close: () => void
  setRoutes: (routes: MapRoute[]) => void
}

export const AboutOrder = ({ id, close, setRoutes }: AboutOrderProps) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const order = useAppSelector(selectOrderById(id), shallowEqual)
  const driver = useAppSelector(order?.driver_id ? selectDriverById(order.driver_id) : () => undefined, shallowEqual)

  useEffect(() => {
    if (!id) return
    batch(() => {
      dispatch(setFocusedOrder(id))
      dispatch(setFocusedOrderArea(null))
    })
  }, [id, dispatch])

  useEffect(() => {
    if (!order) return

    const routes: MapRoute[] = []
    const currentPoint = OrderUtils.getCurrentPoint(order)
    const isDriverWithLocation = driver?.lat && driver?.lng

    if (isDriverWithLocation) {
      dispatch(setFocusedDriver(driver!.id))
    } else {
      dispatch(setFocusedDriver(null))
    }

    const isOrderInProgress = !OrderUtils.isFinished(order)

    if (isDriverWithLocation && isOrderInProgress) {
      if (currentPoint && OrderUtils.isPointPickup(currentPoint)) {
        if (order?.delivery) {
          routes.push({
            from: [order.pickup!.lat, order.pickup!.lng],
            to: [order.delivery.lat, order.delivery.lng],
            transport: driver!.transport_type,
            color: order.delivery.state,
          })
        }
        routes.push({
          from: [driver!.lat, driver!.lng],
          to: [order.pickup!.lat, order.pickup!.lng],
          transport: driver!.transport_type,
          color: order.pickup!.state,
        })
      } else if (currentPoint && OrderUtils.isPointDelivery(currentPoint)) {
        routes.push({
          from: [driver!.lat, driver!.lng],
          to: [order.delivery!.lat, order.delivery!.lng],
          transport: driver!.transport_type,
          color: order.delivery!.state,
        })
      }
    } else {
      if (order.pickup && order.delivery) {
        routes.push({
          from: [order.pickup.lat, order.pickup.lng],
          to: [order.delivery.lat, order.delivery.lng],
          color: order.state,
        })
      }
    }

    // Hack for single-point orders
    if (!routes.length) {
      if (order.pickup) {
        routes.push({
          from: [order.pickup.lat, order.pickup.lng],
          to: [order.pickup.lat, order.pickup.lng],
          color: order.state,
        })
      }
      if (order.delivery) {
        routes.push({
          from: [order.delivery.lat, order.delivery.lng],
          to: [order.delivery.lat, order.delivery.lng],
          color: order.state,
        })
      }
    }

    setRoutes(routes)

    return () => {
      setRoutes([])
    }
  }, [id, order, driver, setRoutes, dispatch])

  useMapViewportSavedOnMount()

  if (!order) return null

  return (
    <div className={cls.panel}>
      <h2 className={cls.title}>#{order.uid}</h2>

      <div className={clsx(cls.section, cls.order, OrderUtils.isSinglePointOrder(order) && cls.singlePointOrder)}>
        {order.pickup && (
          <div className={cls.info}>
            <div className={cls.name}>{order.pickup.name}</div>
            <div className={cls.address}>{order.pickup.address}</div>
          </div>
        )}

        <OrderProgressBar order={order} />

        {order.delivery && (
          <div className={cls.info}>
            <div className={cls.name}>{order.delivery.phone}</div>
            <div className={cls.address}>{order.delivery.address}</div>
          </div>
        )}
      </div>

      <div className={clsx(cls.section, cls.meta)}>
        <div className={cls.pickingTime}>
          <Icon name="delivery-money" color="primary" size="small" />
          Czas kompletacji: {OrderUtils.getPickingTime(order)}
        </div>

        <Button
          title="Zamknij"
          className={cls.close}
          onClick={close}
          variation="text"
          color="primary"
          iconProps={{ name: "close" }}
        />
      </div>

      <div className={clsx(cls.section, cls.driver)}>
        <div className={cls.name}>
          {driver ? (
            <User
              name={driver.name}
              color={DriverUtils.getAvatarColor(driver)}
              indicator={DriverUtils.getAvatarIndicator(driver)}
              dotColor={DriverUtils.getAvatarDotColor(driver)}
              subheader={
                <span className={`status-${OrderUtils.getCurrentState(order)}`}>
                  {`${t(`orders:${OrderUtils.getCurrentState(order)}`)} ${OrderUtils.getPointCounter(order)}`}
                </span>
              }
              context="primary"
            />
          ) : (
            <DriverHeader orderId={order.id} />
          )}
        </div>

        {driver && (
          <div className={cls.overview}>
            <DriverOverview driverId={driver.id} size="medium" />
          </div>
        )}

        {driver && (
          <Tooltip content={t("call")}>
            <span>
              <Button
                as="a"
                href={`tel:${driver.phone}`}
                variation="outlined"
                color="muted"
                iconProps={{ name: "phone", color: "primary" }}
              />
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  )
}
